import { useEffect, useRef, useState } from "react";

import { useEnsureLeadSuggestedContacts } from "./useEnsureLeadSuggestedContacts";

/**
 * Requests contact recommendations for a lead the moment it's opened, so leads created before the
 * feature existed fill in on their own instead of behind a button.
 *
 * The caller decides when a lead qualifies (`enabled`) — chiefly that it has never been asked. The
 * server enforces the same thing, and a lead is only ever asked once: a lead whose recommendations
 * were all dismissed must not have them regenerated, and one whose search found nobody must not pay
 * for that search again on every visit.
 */
export function useAutoGenerateLeadSuggestedContacts({
  leadId,
  enabled,
  onRequested,
}: {
  leadId: string;
  enabled: boolean;
  onRequested: () => void;
}): boolean {
  const { mutate: ensureSuggestions } = useEnsureLeadSuggestedContacts();
  // Which lead's request has come back. Held as an id rather than a flag so navigating to another
  // lead can't inherit the previous one's outcome.
  const [settledFor, setSettledFor] = useState<string | null>(null);
  const requestedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || requestedFor.current === leadId) return;
    requestedFor.current = leadId;

    ensureSuggestions(
      { leadId },
      {
        onSuccess: (data) => {
          if (data.ensureLeadSuggestedContacts) onRequested();
        },
        // Keyed off the variables the call was made with, not the current render's `leadId`, so a
        // response arriving after the user has moved on settles the lead it belongs to.
        onSettled: (_data, _error, variables) => setSettledFor(variables.leadId),
      },
    );
  }, [enabled, leadId, ensureSuggestions, onRequested]);

  return enabled && settledFor !== leadId;
}
